import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  changeRequestSchema,
  MODERATION_SLOTS,
  moderationCardSchema,
  moderationVersionSchema,
  modifierGroupCardSchema,
  type ChangeRequest,
  type ModerationCard,
  type ModerationVersion,
  type ModifierGroupCard,
} from '../model/schema';
import type { ModerationListFilter } from '../model/list-params';
import {
  MODERATION_ENTITY_TYPES,
  type ModerationEntityType,
} from '../lib/moderation-config';

/**
 * Moderation admin API on the Eats host (`VITE_EATS_API_URL`, paths carry
 * `/api/v1`). Every path here is specified in `openapi-eats-admin.json`:
 *
 * - queue: `GET /admin/change-requests` — flat `v1ChangeRequest` rows under
 *   the envelope's `list`, filtered by `entity_type`/`status`/`source`;
 * - one request: `GET /admin/change-requests/{id}` (`{change_request}`);
 * - review card: `GET /admin/<plural>/{id}/moderations` — the slotted
 *   `{prod, manual, sync}` card nested under `data`;
 * - decisions are entity-agnostic (act on the change request):
 *   `POST /admin/change-requests/{id}/approve` (`{payload_patch?}`) and
 *   `POST /admin/change-requests/{id}/reject` (`{reason}` required);
 * - admin publication: `POST /admin/entities/{type}/{id}/publish` (`{}`)
 *   and `…/unpublish` (`{reason?}`).
 */

const eatsBase = { baseURL: env.VITE_EATS_API_URL };

const LIST_PATH = '/api/v1/admin/change-requests';

export interface ModerationListResult {
  data: ChangeRequest[];
  meta: { page: number; pageSize: number; totalPages: number; total: number };
}

function toQuery(filter: ModerationListFilter): Record<string, string> {
  const query: Record<string, string> = {};
  if (filter.entityType) query.entity_type = filter.entityType;
  if (filter.status) query.status = filter.status;
  if (filter.source) query.source = filter.source;
  if (filter.merchantId) query.merchant_id = filter.merchantId;
  if (filter.restaurantId) query.restaurant_id = filter.restaurantId;
  query['pagination.limit'] = String(filter.limit);
  query['pagination.offset'] = String(filter.offset);
  return query;
}

export interface ModerationCategoryOption {
  id: string;
  name: string;
}

/** Product categories used by the products moderation filter. */
export async function listModerationProductCategories(): Promise<
  ModerationCategoryOption[]
> {
  const raw = await http.get<unknown>('/api/v1/admin/product-categories', {
    ...eatsBase,
    query: { 'pagination.limit': '100', 'pagination.offset': '0' },
  });
  const root = (raw ?? {}) as { list?: unknown; data?: { list?: unknown } };
  const rows = Array.isArray(root.list)
    ? root.list
    : Array.isArray(root.data?.list)
      ? root.data.list
      : [];
  return rows.flatMap((row) => {
    const item = (row ?? {}) as { id?: unknown; name?: unknown };
    if (typeof item.id !== 'string') return [];
    const name = item.name;
    const localized =
      typeof name === 'object' && name !== null ? (name as Record<string, unknown>) : {};
    const localizedName = [localized.uz, localized.ru, localized.en].find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0,
    );
    return [{ id: item.id, name: localizedName ?? item.id }];
  });
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Normalize the queue envelope (`{data: {}, list: v1ChangeRequest[], meta}`).
 * The raw gRPC shape (`{change_requests, pagination}`) is accepted as well.
 * Every row parses on its own — one malformed row must not void the page.
 */
export function normalizeModerationList(
  raw: unknown,
  filter: ModerationListFilter,
): ModerationListResult {
  const root = asRecord(raw);
  const listCandidate = [root.list, root.change_requests].find((value) =>
    Array.isArray(value),
  ) as unknown[] | undefined;
  const rows: ChangeRequest[] = [];
  for (const item of listCandidate ?? []) {
    const parsed = changeRequestSchema.safeParse(item);
    if (parsed.success) rows.push(parsed.data);
  }
  const meta = asRecord(root.meta ?? root.pagination);
  const total =
    Number(meta.total ?? meta.total_count ?? meta.count) ||
    (filter.offset === 0 ? rows.length : filter.offset + rows.length);
  const totalPages =
    Number(meta.page_count ?? meta.total_pages) ||
    (filter.limit > 0 ? Math.ceil(total / filter.limit) : 0);
  return {
    data: rows,
    meta: {
      page: filter.limit > 0 ? Math.floor(filter.offset / filter.limit) + 1 : 1,
      pageSize: filter.limit,
      totalPages,
      total,
    },
  };
}

function parseVersion(
  value: unknown,
  kind: ModerationVersion['kind'],
): ModerationVersion | null {
  if (value === null || value === undefined) return null;
  const parsed = moderationVersionSchema.safeParse(value);
  if (!parsed.success) return null;
  // A slot with nothing in it is absent per the spec; an empty object is
  // tolerated as absent too rather than rendered as a blank version.
  if (!parsed.data.entity && !parsed.data.change_request) return null;
  return { ...parsed.data, kind };
}

function parseSlots(bag: Record<string, unknown>): ModifierGroupCard {
  const card: ModifierGroupCard = {};
  for (const slot of MODERATION_SLOTS) {
    const version = parseVersion(bag[slot], slot);
    if (version) card[slot] = version;
  }
  return card;
}

/**
 * Normalize the review card. Per spec the card travels nested under `data`
 * (the envelope only hoists a LONE top-level array into `list`), so `data`
 * is read first and the bare object is the fallback. Slots parse
 * independently — a malformed `sync` slot must not hide the `prod` one —
 * and each version is stamped with its slot `kind`.
 */
export function normalizeModerationCard(raw: unknown): ModerationCard {
  const root = asRecord(raw);
  const data = asRecord(root.data);
  const bag = MODERATION_SLOTS.some((slot) => slot in data) ? data : root;
  const groups = Array.isArray(bag.modifier_groups) ? bag.modifier_groups : [];
  const card = moderationCardSchema.parse({});
  return {
    ...card,
    ...parseSlots(bag),
    modifier_groups: groups.flatMap((group) => {
      const slots = parseSlots(asRecord(group));
      const parsed = modifierGroupCardSchema.safeParse(slots);
      return parsed.success && (slots.prod || slots.manual || slots.sync)
        ? [parsed.data]
        : [];
    }),
  };
}

/** `{data: {change_request}}` / `{change_request}` / bare request → request. */
export function unwrapChangeRequest(raw: unknown): ChangeRequest {
  const root = asRecord(raw);
  const nested = asRecord(root.data);
  const candidate = nested.change_request ?? root.change_request ?? raw;
  return changeRequestSchema.parse(candidate);
}

export interface ApproveChangeRequestArgs {
  changeRequestId: string;
  /** Reviewer amendment merged into the payload before it applies. */
  payloadPatch?: Record<string, unknown> | undefined;
}

export const moderationApi = {
  async list(
    entityType: ModerationEntityType,
    filter: ModerationListFilter,
  ): Promise<ModerationListResult> {
    const raw = await http.get<unknown>(LIST_PATH, {
      ...eatsBase,
      query: toQuery({
        ...filter,
        entityType: MODERATION_ENTITY_TYPES[entityType].queueEntityType,
      }),
    });
    return normalizeModerationList(raw, filter);
  },

  async getChangeRequest(changeRequestId: string): Promise<ChangeRequest> {
    const raw = await http.get<unknown>(`${LIST_PATH}/${changeRequestId}`, eatsBase);
    return unwrapChangeRequest(raw);
  },

  async getCard(
    entityType: ModerationEntityType,
    entityId: string,
  ): Promise<ModerationCard> {
    const { cardPath } = MODERATION_ENTITY_TYPES[entityType];
    const raw = await http.get<unknown>(cardPath(entityId), eatsBase);
    return normalizeModerationCard(raw);
  },

  async approve({
    changeRequestId,
    payloadPatch,
  }: ApproveChangeRequestArgs): Promise<ChangeRequest> {
    const raw = await http.post<unknown>(
      `${LIST_PATH}/${changeRequestId}/approve`,
      payloadPatch ? { payload_patch: payloadPatch } : {},
      eatsBase,
    );
    return unwrapChangeRequest(raw);
  },

  async reject(changeRequestId: string, reason: string): Promise<ChangeRequest> {
    const raw = await http.post<unknown>(
      `${LIST_PATH}/${changeRequestId}/reject`,
      { reason },
      eatsBase,
    );
    return unwrapChangeRequest(raw);
  },

  async publish(entityType: ModerationEntityType, entityId: string): Promise<unknown> {
    return http.post(
      `/api/v1/admin/entities/${MODERATION_ENTITY_TYPES[entityType].queueEntityType}/${entityId}/publish`,
      {},
      eatsBase,
    );
  },

  async unpublish(
    entityType: ModerationEntityType,
    entityId: string,
    reason?: string,
  ): Promise<unknown> {
    const trimmed = reason?.trim() ?? '';
    return http.post(
      `/api/v1/admin/entities/${MODERATION_ENTITY_TYPES[entityType].queueEntityType}/${entityId}/unpublish`,
      trimmed === '' ? {} : { reason: trimmed },
      eatsBase,
    );
  },
};
